import clsx from "clsx";
import Link from "next/link";
import { useEffect } from "react";

import { Button, Card } from "@chef/components";
import { BRAND_PATHS } from "@chef/constants";
import { Calendar, Delivery } from "@chef/icons/large";
import { useBillingQuery } from "@chef/state-management";
import { useTrack } from "@chef/state-management/hooks";

import { intl } from "./PauseSubscriptionPage.Intl";

export const PauseSubscriptionPage = () => {
  const track = useTrack();

  const { data: billingQuery } = useBillingQuery();

  const hasDiscountsToBeLost = billingQuery?.billing.discounts.some(
    ({ disableOnFreeze, usesLeft, validEndDate }) => {
      if (!disableOnFreeze) {
        return false;
      }
      if (usesLeft <= 0) {
        return false;
      }
      if (new Date(validEndDate) <= new Date()) {
        return false;
      }
      return true;
    },
  );

  useEffect(() => {
    track("updateSubscriptionStepViewed", {
      affiliation: "Frontend process",
      change_subscription_details: "Pause subscription",
      url: window.location.href,
    });
  }, [track]);

  const trackCompleted = () => {
    track("updateSubscriptionStepCompleted", {
      affiliation: "Frontend process",
      changed_subscription_details: "Pause subscription",
      url: window.location.href,
    });
  };

  return (
    <>
      <div className="text-center lg:mt-12 col-span-full">
        <h1 className="text-2xl">
          <strong>{intl.OTHER_WAYS_WE_CAN_HELP_YOU}</strong>
        </h1>
        <p className="mt-2">
          {intl.THESE_TWO_POSSIBILITIES_MAKES_IT_EASIER_FOR_YOU}
        </p>
      </div>

      <ActionCard
        className="col-span-full md:col-span-3 md:col-start-2 xl:col-start-4"
        Icon={<Calendar />}
        headerText={intl.PAUSE_FOR_PERIOD}
        subHeaderText={intl.GOING_ON_VACATION}
        buttonText={intl.PAUSE_SUBSCRIPTION}
        link={BRAND_PATHS.PAUSE_SUBSCRIPTION_FOR_PERIOD_PAGE_PATH}
        disclaimer={hasDiscountsToBeLost ? intl.KEEP_YOUR_DISCOUNTS : undefined}
        onButtonClick={trackCompleted}
      />

      <ActionCard
        className="col-span-full md:col-span-3"
        Icon={<Delivery />}
        headerText={intl.CHANGE_INTERVAL}
        subHeaderText={intl.RECEIVING_DELIVERIES_TOO_OFTEN}
        buttonText={intl.CHANGE_DELIVERY_INTERVAL}
        link={BRAND_PATHS.CHANGE_ACTIVE_ADDRESS_PAGE}
        disclaimer={hasDiscountsToBeLost ? intl.KEEP_YOUR_DISCOUNTS : undefined}
        onButtonClick={trackCompleted}
      />

      <div className="flex flex-col items-center mt-8 lg:mt-10 col-span-full">
        <p>
          <strong>{intl.DO_YOU_STILL_WANT_TO_CANCEL_YOUR_SUBSCRIPTION}</strong>
        </p>
        <Link
          href={BRAND_PATHS.PAUSE_SUBSCRIPTION_QUIZ_PAGE}
          passHref
          legacyBehavior
        >
          <Button className="mt-4 bg-white" outlined onClick={trackCompleted}>
            {intl.CANCEL_SUBSCRIPTION}
          </Button>
        </Link>
      </div>
    </>
  );
};

const ActionCard = ({
  className,
  headerText,
  Icon,
  subHeaderText,
  buttonText,
  link,
  disclaimer,
  onButtonClick,
}: {
  className?: string;
  headerText: string;
  Icon: JSX.Element;
  subHeaderText: string;
  buttonText: string;
  link: string;
  disclaimer?: string;
  onButtonClick?: () => void;
}) => {
  return (
    <Card
      className={clsx(
        "flex flex-col items-center py-4 lg:py-6 px-6",
        className,
      )}
      noPadding
    >
      <div className="flex items-center justify-center w-20 h-20 text-4xl rounded-full bg-grey-3">
        {Icon}
      </div>

      <p className="mt-6 font-serif text-lg text-center lg:text-xl">
        <strong>{headerText}</strong>
      </p>

      <p className="mt-2 grow">{subHeaderText}</p>

      <Link href={link} legacyBehavior>
        <Button className="mt-6" primary onClick={onButtonClick}>
          {buttonText}
        </Button>
      </Link>

      <p className="mt-4 text-sm text-discount">{disclaimer}</p>
    </Card>
  );
};
